import { GraphQLError } from 'graphql';
import store from '.';
import {
  AlcoholUseOptions,
  DrugUseOptions,
  SpecialtyCarePhrases,
} from '../clinical/types';
import {
  PatientEmergencyContactInput,
  PatientGuardianInput,
  PatientInput,
  PatientPharmacyInput,
  PatientPrimaryPolicyholderInput,
  PatientShouldNotBeMatchedReason,
  Provider,
  ProviderNotMatchReason,
} from '../generated/graphql';

export type RootState = ReturnType<typeof store.getState>;
export type GetState = () => RootState;
export type AppDispatch = typeof store.dispatch;

export type ThunkReturn = void | readonly GraphQLError[];
export type Thunk = (
  dispatch: AppDispatch,
  getState?: GetState
) => Promise<ThunkReturn>;

export type DispatchUpdateAssessmentInput = {
  questionCode: string;
  answerValue: string[];
}[];

// "// * (see above)" -- comment indicates that the odd name was done to match
// the previous state machine, and we may want to change these
export enum Screens {
  LandingRouter = 'landingRouter',
  Introduction = 'introduction',
  IntroScreenOne = 'IntroScreenOne',
  IntroScreenTwo = 'introScreenTwo',
  IntroScreenThree = 'introScreenThree',
  IntroScreenFour = 'introScreenFour',
  ZocdocIntroductionFirst = 'zocdocIntroductionFirst',
  ZocdocIntroductionSecond = 'zocdocIntroductionSecond',
  GetStarted = 'getStarted',
  CollectEmail = 'collectEmail',
  ConfirmEmail = 'confirmEmail',
  PreviouslyCompleted = 'previouslyCompleted',
  PatientType = 'patientType',
  PersonalDetails = 'personalDetails',
  StateCheck = 'stateCheck',
  CollectDemographics = 'collectDemographics',
  CompletedByMinorTermination = 'CompletedByMinorTermination',
  NotAMinorTermination = 'NotAMinorTermination',
  ConsentReferrerSharing = 'consentReferrerSharing',
  ConsentForMinor = 'consentForMinor',
  GuardianDemographics = 'guardianDemographics',
  RV_MainReason = 'RV_MainReason',
  RV_SecondaryReason = 'RV_SecondaryReason',
  RV_SpecialtyCare = 'RV_SpecialtyCare',
  RV_UseOfDrugs = 'RV_UseOfDrugs',
  RV_DrinkingAlcohol = 'RV_DrinkingAlcohol',
  RV_KillingYourself = 'RV_KillingYourself',
  RV_PsychiatricImpatient = 'RV_PsychiatricImpatient',
  SensitiveInformationWarning = 'sensitiveInformationWarning',
  OutOfCoverage = 'outOfCoverage',
  Emergency = 'emergency',
  NoService = 'noService',
  MedicalInfoIntro = 'medicalInfoIntro',
  MI_InsuranceCombo = 'MI_InsuranceCombo',
  MI_InsuranceForm = 'MI_InsuranceForm',
  MI_InsuranceId = 'MI_InsuranceId',
  NotInNetwork = 'notInNetwork',
  MI_MedicationPharmacyCombo = 'MI_MedicationPharmacyCombo',
  InNetwork = 'inNetwork',
  MI_HaveAppointment = 'MI_HaveAppointment',
  MI_PrimaryPolicyHolder = 'MI_PrimaryPolicyHolder',
  MI_EmergencyContact = 'MI_EmergencyContact',
  Review = 'review',
  MatchLoading = 'matchLoading',
  List = 'list', // * (see above)
  AppointmentConfirmation = 'AppointmentConfirmation', // * (see above)
  NoMatch = 'noMatch',
  Finish = 'finish', // * (see above)
}

export type UpdateMetaArgs = {
  screen?: Screens;
  hasPreviouslyCompleted?: boolean;
};
export type GetTarget = (
  state?: RootState,
  nonStateContext?: Omit<UpdateMetaArgs, 'screen'>
) => Screens;
export type MetaTargetConfig = Partial<Record<Screens, GetTarget>>;

export enum ReferralSource {
  Zocdoc = 'zocdoc_capi',
  HCA = 'HCA',
}

export type SessionState = {
  sessionPatientId: string;
  shortCode: string;
  documentId: string;
  wasReferredByZocdoc: boolean;
  referralSource: ReferralSource | string;
  user: Record<string, any>;
  isGuardian: boolean;
};

export type MatchingState = {
  matches: Provider[];
  provider: Partial<Provider>;
  zocdocProvider: Partial<Provider>;
  appointmentTime: string;
  patientShouldNotBeMatchedReasons: PatientShouldNotBeMatchedReason[];
  zdProviderMatchFailureReasons: ProviderNotMatchReason[];
  zdProviderTreatmentDealbreakerReasons: string[];
};

export enum QuestionTypes {
  One = 'one',
  Many = 'many',
}

export enum AssessmentSections {
  Intro = 'intro',
  Onboarding = 'onboarding',
  Clinical = 'clinical',
  Booking = 'booking',
}

export type MetaState = {
  canGoBack?: boolean;
  stepTitleIsNotPHI: boolean;
  screen: Screens;
  subTitle?: string;
  label?: string;
  subLabel?: string;
  type?: QuestionTypes;
  options?: string[];
  canRewrite?: boolean;
  review?: boolean;
  step?: boolean;
  secondaryText?: string;
  isTerminate?: boolean;
  section?: string;
  hasPreviouslyCompletedAssessment?: boolean;
  assessmentSection?: AssessmentSections;
  isSectionIntro?: boolean;
};

type MainReasonVisitKey =
  | 'Depression'
  | 'Anxiety'
  | 'ADHD'
  | 'Bipolar Disorder'
  | 'Bipolar disorder' // we need to unify these between the types and the specialty entries in the db
  | 'OCD'
  | 'PTSD'
  | "I'm not sure";

type SecondaryReasonVisitKey =
  | 'Self-harm'
  | 'Eating disorder'
  | 'Substance Use'
  | 'Schizophrenia'
  | 'Substance use disorder'
  | 'Personality disorder'
  | 'No, none of these apply';

// Update here after v2 v2.1 convergence
export type ReasonVisitAnswers = {
  [Screens.RV_MainReason]?: MainReasonVisitKey[];
  RV_MainReasonFreeText?: string[];
  [Screens.RV_SecondaryReason]?: SecondaryReasonVisitKey[];
  [Screens.RV_SpecialtyCare]?: SpecialtyCarePhrases[];
  [Screens.RV_PsychiatricImpatient]?: ('Yes' | 'No')[];
  [Screens.RV_KillingYourself]?: ('Yes' | 'No')[];
  [Screens.RV_DrinkingAlcohol]?: AlcoholUseOptions[];
  [Screens.RV_UseOfDrugs]?: DrugUseOptions[];
};

export type MedicalInfoAnswers = {
  MI_InsuranceProvider?: string[];
  MI_InsurancePlan?: string[];
  MI_InsuranceId?: string[];
  MI_PrimaryPolicyHolder?: ('Yes' | 'No')[];
  MI_PrimaryPolicyHolderDetails?: PatientPrimaryPolicyholderInput;
  MI_PrescriptionMedication?: ('Yes' | 'No')[];
  MI_MedicationList?: any[];
  MI_OpenPsychiatricMeds?: ('Yes' | 'No')[];
  [Screens.MI_MedicationPharmacyCombo]?: ('Yes' | 'No')[];
  MI_PrimaryPharmacy?: ('Yes' | 'No')[];
  MI_PrimaryPharmacyDetails?: PatientPharmacyInput;
  [Screens.MI_EmergencyContact]?: PatientEmergencyContactInput;
  [Screens.MI_HaveAppointment]?: ('Yes' | 'No')[];
};

export type GuardianDemographicsState = {
  primary?: {
    consentForMinor?: boolean;
  } & PatientGuardianInput;
  secondary?: PatientGuardianInput;
};

export type AssessmentState = {
  patient: PatientInput;
  guardians?: GuardianDemographicsState;
  reasonVisitAnswers: ReasonVisitAnswers;
  medicalInfoAnswers: MedicalInfoAnswers;
};
